/**
 * PWA 单实例管理
 *
 * 防止多个 PWA 实例同时运行，避免 WebHID 设备状态冲突
 *
 * 工作原理：
 * 1. 只在 PWA 独立窗口模式下启用（standalone/fullscreen/minimal-ui）
 * 2. 使用 BroadcastChannel 在 PWA 窗口间通信
 * 3. 新 PWA 窗口启动时检查是否已有 PWA 窗口
 * 4. 如果已有 PWA 窗口，显示提示界面让用户选择
 * 5. 浏览器标签页不受限制
 */

const CHANNEL_NAME = 'aiiifly-single-instance'
const HEARTBEAT_INTERVAL = 5000 // 5秒心跳

interface InstanceMessage {
  type: 'ping' | 'pong' | 'im-alive'
  instanceId: string
  timestamp: number
  isPwa: boolean
}

class SingleInstanceManager {
  private channel: BroadcastChannel | null = null
  private instanceId: string
  private heartbeatTimer: number | null = null
  private isActive = false
  private isPwaWindow = false

  constructor() {
    this.instanceId = `instance-${Date.now()}-${Math.random().toString(36).slice(2)}`
    this.isPwaWindow = this.checkIfPwaWindow()
  }

  /**
   * 检查是否为 PWA 独立窗口模式
   */
  private checkIfPwaWindow(): boolean {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches
    const isMinimalUi = window.matchMedia('(display-mode: minimal-ui)').matches
    const nav = navigator as Navigator & { standalone?: boolean }
    const isIosStandalone = nav.standalone === true

    return isStandalone || isFullscreen || isMinimalUi || isIosStandalone
  }

  /**
   * 初始化单实例检查
   * @returns true 如果当前实例可以继续运行，false 如果需要显示提示界面
   */
  async initialize(): Promise<boolean> {
    // 只在 PWA 窗口模式下启用单实例限制
    if (!this.isPwaWindow) {
      console.log('[SingleInstance] Running in browser mode, multi-instance allowed')
      return true
    }

    if (!('BroadcastChannel' in window)) {
      console.warn('[SingleInstance] BroadcastChannel not supported, multi-instance prevention disabled')
      return true
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME)
    this.setupMessageHandler()

    // 检查是否已有活跃的 PWA 实例
    const hasExisting = await this.checkForExistingInstance()

    if (hasExisting) {
      // 已有 PWA 实例，返回 false 以显示提示界面
      console.log('[SingleInstance] Existing PWA instance detected')
      return false
    }

    // 没有其他 PWA 实例，成为主实例
    this.isActive = true
    this.startHeartbeat()
    console.log('[SingleInstance] Became primary PWA instance')

    return true
  }

  private setupMessageHandler(): void {
    if (!this.channel) return

    this.channel.onmessage = (event: MessageEvent<InstanceMessage>) => {
      const msg = event.data

      // 只响应来自其他 PWA 窗口的消息
      if (!msg.isPwa || msg.instanceId === this.instanceId) return

      switch (msg.type) {
        case 'ping':
          // 有新 PWA 实例在检查，回应表明我们存在
          if (this.isActive && this.isPwaWindow) {
            this.sendMessage({
              type: 'pong',
              instanceId: this.instanceId,
              timestamp: Date.now(),
              isPwa: true
            })
          }
          break
      }
    }
  }

  private async checkForExistingInstance(): Promise<boolean> {
    return new Promise((resolve) => {
      let responseReceived = false
      let checkTimer: number | null = null

      const messageHandler = (event: MessageEvent<InstanceMessage>) => {
        const msg = event.data

        // 只接受来自其他 PWA 窗口的响应
        if (!msg.isPwa || msg.instanceId === this.instanceId) return

        if (msg.type === 'pong' || msg.type === 'im-alive') {
          responseReceived = true
          cleanup()
          resolve(true)
        }
      }

      const cleanup = () => {
        if (this.channel) {
          this.channel.removeEventListener('message', messageHandler)
        }
        if (checkTimer !== null) {
          clearTimeout(checkTimer)
          checkTimer = null
        }
      }

      if (this.channel) {
        this.channel.addEventListener('message', messageHandler)
      }

      // 发送 ping 消息，标记为 PWA 窗口
      this.sendMessage({
        type: 'ping',
        instanceId: this.instanceId,
        timestamp: Date.now(),
        isPwa: true
      })

      // 等待 500ms 看是否有回应
      checkTimer = window.setTimeout(() => {
        cleanup()
        resolve(responseReceived)
      }, 500)
    })
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()

    this.heartbeatTimer = window.setInterval(() => {
      if (this.isActive && this.isPwaWindow) {
        this.sendMessage({
          type: 'im-alive',
          instanceId: this.instanceId,
          timestamp: Date.now(),
          isPwa: true
        })
      }
    }, HEARTBEAT_INTERVAL)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private sendMessage(msg: InstanceMessage): void {
    if (this.channel) {
      try {
        this.channel.postMessage(msg)
      } catch (error) {
        console.warn('[SingleInstance] Failed to send message:', error)
      }
    }
  }

  /**
   * 清理资源
   */
  destroy(): void {
    this.isActive = false
    this.stopHeartbeat()

    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
  }
}

// 单例
let manager: SingleInstanceManager | null = null

/**
 * 初始化单实例检查
 * @returns Promise<boolean> true 表示当前实例可以继续，false 表示需要显示提示界面
 */
export async function initSingleInstance(): Promise<boolean> {
  if (manager) {
    console.warn('[SingleInstance] Already initialized')
    return true
  }

  manager = new SingleInstanceManager()
  return await manager.initialize()
}

/**
 * 清理单实例管理器
 */
export function destroySingleInstance(): void {
  if (manager) {
    manager.destroy()
    manager = null
  }
}
